import React, { useEffect, useState } from 'react';
import { meetings, projects as projectsApi } from '@/utils/api';
import { Mic, Upload, Trash2, X, Sparkles, FileText } from 'lucide-react';
import { toast } from 'sonner';

export default function Meetings() {
  const [list, setList] = useState([]);
  const [projects, setProjects] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ title: '', project_id: '', transcript: '' });
  const [selected, setSelected] = useState(null);

  const reload = () => meetings.list().then(r => setList(r.data));
  useEffect(() => {
    reload();
    projectsApi.list().then(r => setProjects(r.data));
  }, []);

  const upload = async () => {
    if (!form.title || !form.transcript) { toast.error('Need title + transcript'); return; }
    setUploading(true);
    try {
      await meetings.upload(form);
      toast.success('Meeting analyzed');
      setForm({ title: '', project_id: '', transcript: '' });
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setUploading(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete meeting?')) return;
    await meetings.delete(id);
    reload();
  };

  const openDetail = async (m) => {
    const r = await meetings.get(m.id);
    setSelected(r.data);
  };

  return (
    <div className="p-6 animate-fade-in" data-testid="meetings-page">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-btn flex items-center justify-center" style={{ background: '#5B2D8E20' }}>
          <Mic size={18} style={{ color: '#5B2D8E' }} />
        </div>
        <div>
          <h1 className="text-xl font-black" style={{ color: 'var(--ink)' }}>Meeting Notetaker</h1>
          <p className="text-sm" style={{ color: 'var(--muted)' }}>Paste a transcript, get AI summary + action items</p>
        </div>
      </div>

      <div className="card-base p-4 mb-6" data-testid="meeting-upload-form">
        <h3 className="font-bold text-sm mb-3 flex items-center gap-1.5" style={{ color: 'var(--ink)' }}>
          <Upload size={14} /> New meeting
        </h3>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <input className="input-agency text-sm" placeholder="Meeting title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="meeting-title" />
          <select className="input-agency text-sm" value={form.project_id} onChange={e => setForm({...form, project_id: e.target.value})}>
            <option value="">(No project)</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <textarea className="input-agency text-sm resize-none" rows={8} placeholder="Paste transcript here (Meet, Zoom, etc)..." value={form.transcript} onChange={e => setForm({...form, transcript: e.target.value})} data-testid="meeting-transcript" />
        <button onClick={upload} disabled={uploading} className="btn-primary mt-3 text-sm flex items-center gap-2" data-testid="meeting-analyze">
          <Sparkles size={13} /> {uploading ? 'Analyzing…' : 'Analyze with Claude'}
        </button>
      </div>

      <h3 className="font-bold text-sm mb-3" style={{ color: 'var(--ink)' }}>Recent meetings</h3>
      {list.length === 0 ? (
        <div className="card-base p-8 text-center">
          <FileText size={28} style={{ color: 'var(--border)', margin: '0 auto 10px' }} />
          <p className="text-sm" style={{ color: 'var(--muted)' }}>No meetings yet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {list.map(m => (
            <div key={m.id} className="card-interactive p-3" onClick={() => openDetail(m)} data-testid={`meeting-${m.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-bold text-sm" style={{ color: 'var(--ink)' }}>{m.title}</p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--muted)' }}>{new Date(m.created_at).toLocaleString()}</p>
                  {m.notes?.action_items?.length > 0 && (
                    <p className="text-xs mt-1" style={{ color: '#3C8A5A' }}>
                      {m.notes.action_items.length} action item{m.notes.action_items.length !== 1 ? 's' : ''}
                    </p>
                  )}
                </div>
                <button onClick={(e) => { e.stopPropagation(); del(m.id); }} style={{ color: '#B34A3A' }}><Trash2 size={12} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="w-full max-w-3xl rounded-card my-8 p-5" style={{ background: 'var(--surface)' }}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-black text-lg" style={{ color: 'var(--ink)' }}>{selected.title}</h2>
              <button onClick={() => setSelected(null)} style={{ color: 'var(--muted)' }}><X size={18} /></button>
            </div>
            <div className="space-y-4">
              {selected.notes?.summary && (
                <div className="p-3 rounded-btn" style={{ background: 'var(--bg)' }}>
                  <p className="text-xs font-bold uppercase mb-2" style={{ color: 'var(--muted)' }}>Summary</p>
                  <p className="text-sm whitespace-pre-wrap" style={{ color: 'var(--ink)' }}>{selected.notes.summary}</p>
                </div>
              )}
              {selected.notes?.action_items?.length > 0 && (
                <div>
                  <p className="text-xs font-bold uppercase mb-2" style={{ color: 'var(--muted)' }}>Action items</p>
                  <ul className="space-y-1.5">
                    {selected.notes.action_items.map((a, i) => (
                      <li key={i} className="p-2 rounded-btn text-sm flex items-center justify-between" style={{ background: '#3C8A5A10' }}>
                        <span style={{ color: 'var(--ink)' }}>{typeof a === 'string' ? a : a.task}</span>
                        {typeof a === 'object' && a.owner && <span className="badge-status" style={{ background: '#3C8A5A15', color: '#3C8A5A' }}>{a.owner}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {selected.notes?.decisions?.length > 0 && (
                <div>
                  <p className="text-xs font-bold uppercase mb-2" style={{ color: 'var(--muted)' }}>Decisions</p>
                  <ul className="text-sm space-y-1" style={{ color: 'var(--ink)' }}>
                    {selected.notes.decisions.map((d, i) => <li key={i}>• {d}</li>)}
                  </ul>
                </div>
              )}
              {selected.notes?.risks?.length > 0 && (
                <div>
                  <p className="text-xs font-bold uppercase mb-2" style={{ color: 'var(--muted)' }}>Risks</p>
                  <ul className="text-sm space-y-1" style={{ color: '#B34A3A' }}>
                    {selected.notes.risks.map((r, i) => <li key={i}>⚠ {r}</li>)}
                  </ul>
                </div>
              )}
              <details className="text-xs" style={{ color: 'var(--muted)' }}>
                <summary className="cursor-pointer">Raw transcript</summary>
                <pre className="whitespace-pre-wrap mt-2" style={{ fontFamily: 'monospace' }}>{selected.transcript}</pre>
              </details>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
